import { SessionSettingsModel, ThemeModel } from "api/Auth";
import { Constants } from "assets/appConfig";
import i18next from 'i18next';
import { getCookie } from "lib/CookieUtils";
import LibUtils from "lib/LibUtils";
import { AccountType, DynamicSystemRoles } from "models/ApiEnums";
import { GetUserAccountDto, JsonWebToken, RefreshToken, UserSessionInfo } from "models/ApiModels";
import { Cultures } from "models/AppModels";
import { RouteList, RoutePaths } from "routes/RouteConfig";

///////////////////////////////////////////////////////////////////////////////
const STORAGE_USER_KEY = "user";
const STORAGE_CULTURE_KEY = "culture";
const STORAGE_KEEP_CONNECTED = "connected";
const STORAGE_CONFIG_KEY = "config";
const STORAGE_REFRESH_TOKEN = "rt";
const STORAGE_TOKEN_VALIDTO = "tvt";
const STORAGE_VERSION = 'GenieVersion';
const STORAGE_LOGGED = 'l';
export const DefaultCulture = "pt-BR";
///////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////////////////////////////////////////
type SessionObserverCallback = () => void;
///////////////////////////////////////////////////////////////////////////////

export default class Session {

    private static themeSettings: ThemeModel;
    private static sessionSettings: SessionSettingsModel;

    //////////////////////////////////////////

    private static internalSetUserSession(sessionInfo: UserSessionInfo) {
        if (sessionInfo.keepConnected) {
            window.localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(sessionInfo));
        }
        else {
            window.localStorage.removeItem(STORAGE_USER_KEY);
        }
        Session.userCache = sessionInfo;
        Session.notifyObservers();
    }

    private static internalSetUiCulture(culture: string) {
        if (window.localStorage.getItem(STORAGE_CULTURE_KEY) != culture) {
            window.localStorage.setItem(STORAGE_CULTURE_KEY, culture);
            i18next.changeLanguage(culture);
        }
    }

    private static getMainWindow() {
        return window.opener || window;
    }

    private static set userCache(info: UserSessionInfo) {
        // Armazenamento duplo:
        //   1. Em memória para evitar execução de JSON.pase "n" vezes.
        //   2. SessionStorage funciona como um "backup" caso o usuário tecle F5 para refresh do navegador.
        Session.getMainWindow()["user"] = info;
        window.sessionStorage.setItem(STORAGE_USER_KEY, JSON.stringify(info));
    }

    private static get userCache(): UserSessionInfo {
        let user = Session.getMainWindow()["user"];
        if (user == null) {
            user = JSON.parse(window.sessionStorage.getItem(STORAGE_USER_KEY)) as UserSessionInfo;
            if (user != null) Session.getMainWindow()["user"] = user;
        }
        return user;
    }

    //////////////////////////////////////////

    static processNewSession(webToken: JsonWebToken) {
        Session.internalSetUserSession(webToken.userSessionInfo);
        Session.internalSetUiCulture(webToken.userSessionInfo.culture);
        Session.processWebToken(webToken);
        window.localStorage.setItem(STORAGE_LOGGED, "1");
    }

    static processWebToken(webToken: JsonWebToken) {
        this.tokenValidTo = webToken.tokenValidTo;
        this.refreshToken = webToken.refreshToken;
    }

    static processSSO(refreshToken: string) {
        this.clearSession();

        const expDate = new Date();
        expDate.setMinutes(expDate.getMinutes() + 2);

        this.refreshToken = { token: refreshToken, expirationDate: expDate } as RefreshToken;
    }

    //////////////////////////////////////////

    static get refreshToken(): RefreshToken {
        const refreshToken = window.localStorage.getItem(STORAGE_REFRESH_TOKEN);
        return refreshToken == null ? null : JSON.parse(refreshToken);
    }

    static set refreshToken(refreshToken: RefreshToken) {
        window.localStorage.setItem(STORAGE_REFRESH_TOKEN, JSON.stringify(refreshToken));
    }

    //////////////////////////////////////////

    static get tokenValidTo(): Date {
        const sdt = window.localStorage.getItem(STORAGE_TOKEN_VALIDTO);
        return (sdt == null) ? new Date(0) : new Date(sdt);
    }

    static set tokenValidTo(expireDate: Date) {
        window.localStorage.setItem(STORAGE_TOKEN_VALIDTO, expireDate.toString());
    }

    //////////////////////////////////////////
    static get bearerAuthorization(): string {
        return null;
        //return `Bearer ${Session.token}`;
    }

    //////////////////////////////////////////

    static get anonymousUser(): UserSessionInfo {
        return {
            id: Constants.EmptyGuid,
            name: "anonymous",
            accountType: AccountType.Local,
            culture: DefaultCulture,
            timezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            roles: 0,
            acceptedCookies: true
        } as UserSessionInfo;
    }

    static get cachedUser(): UserSessionInfo {
        let cache = Session.userCache;
        if (cache == null) {
            const item = window.localStorage.getItem(STORAGE_USER_KEY);
            if (item) cache = Session.userCache = JSON.parse(item) as UserSessionInfo;
        }
        return cache;
    }


    static get user(): UserSessionInfo {
        return Session.cachedUser || Session.anonymousUser;
    }

    static updateUserInfo(account: GetUserAccountDto) {
        var user = Session.cachedUser;
        if (user) {

            user.culture = account.culture;
            user.lastUpdate = account.recordInfo.lastUpdateDate.getTime();
            user.name = account.name;
            user.timezoneId = account.timezoneId;
            if (account.thumbnail != null) user.thumbnailUrl = account.thumbnail.url;

            Session.internalSetUserSession(user);
            Session.internalSetUiCulture(user.culture);
        }
    }

    static updateAcceptCookie() {
        var user = Session.cachedUser;
        if (user) {
            user.acceptedCookies = true;
            Session.internalSetUserSession(user);
        }
    }

    static updateHasOrCanViewCourses(hasOrCanViewCourses: boolean) {
        var user = Session.cachedUser;
        if (user) {
            if (hasOrCanViewCourses) user.dynamicRoles |= DynamicSystemRoles.HasOrCanViewCourses;
            else user.dynamicRoles ^= DynamicSystemRoles.HasOrCanViewCourses;

            Session.internalSetUserSession(user);
        }
    }

    static hasRole(requiredRoles: number): boolean {
        var user = Session.cachedUser;
        return user != null && (user.roles & requiredRoles) > 0;
    }

    static hasDynamicRole(requiredRoles: number): boolean {
        var user = Session.cachedUser;
        return user != null && (user.dynamicRoles & requiredRoles) > 0;
    }

    static get thumbnailUrl() {
        return Session.user.thumbnailUrl;
    };

    //////////////////////////////////////////

    static set uiCulture(culture: string) {
        Session.internalSetUiCulture(culture);
    }

    static get uiCulture(): string {
        let culture = window.localStorage.getItem(STORAGE_CULTURE_KEY);
        if (culture == null) {
            var cultureInfo = Cultures.find(p => p.cultures.indexOf(navigator.language) > -1);
            if (cultureInfo == null) culture = DefaultCulture;
            else culture = navigator.language;
        }
        return culture;
    }

    static get locale(): string {
        var user = Session.cachedUser;
        var locale = user == null ? navigator.language : user.culture;
        if (locale == null || locale == "") locale = DefaultCulture;
        return Cultures.find(p => p.cultures.indexOf(locale) > -1) ? locale.substr(0, 2) : DefaultCulture.substr(0, 2);
    }

    static get timezone(): string {
        var user = Session.cachedUser;
        return user == null ? Intl.DateTimeFormat().resolvedOptions().timeZone : user.timezoneId;
    }

    static get isLocalAccount(): boolean {
        var user = Session.cachedUser;
        return user != null && (user.accountType & AccountType.Local) > 0;
    }
    ///////////////////////////////////////////////////////////

    static get SessionId(): string {
        return window["SessionId"] || this.startClientSession();
    }

    static startClientSession(): string {
        window["SessionId"] = LibUtils.newGuid();
        return window["SessionId"];
    }

    static clearSession(): void {
        Session.userCache = null;
        window["SessionId"] = null;
        window.localStorage.removeItem(STORAGE_REFRESH_TOKEN);
        window.localStorage.removeItem(STORAGE_TOKEN_VALIDTO);
        window.localStorage.removeItem(STORAGE_USER_KEY);
        window.localStorage.removeItem(STORAGE_CULTURE_KEY);
    }

    ///////////////////////////////////////////////////////////

    static set appTheme(themeModel: ThemeModel) {
        window.localStorage.setItem(STORAGE_CONFIG_KEY, JSON.stringify(themeModel));
        Session.themeSettings = themeModel;
        Session.notifyObservers();
    }

    static get appTheme() {
        if (Session.themeSettings == null) {
            const item = window.localStorage.getItem(STORAGE_CONFIG_KEY);
            if (item)
                Session.themeSettings = JSON.parse(item) as ThemeModel;
            else {
                Session.themeSettings = {
                    title: "Genie",
                    themeName: "default",
                    lastUpdateDate: new Date()
                };
            }
        }
        return Session.themeSettings;
    }

    ///////////////////////////////////////////////////////////

    static get isConnected(): boolean {

        // Se está entrando por uma URL Pública, deve retornar como NÃO CONECTADO.
        const href = window.document.location.href.toLowerCase();
        var excludePath = RouteList.find(x => href.indexOf(x.path.replace(":key", "").toLowerCase()) > -1 && x.public == true);
        if (excludePath != null) return false;

        const item = window.localStorage.getItem(STORAGE_KEEP_CONNECTED);   // Feito marcação explícita.
        const sItem = window.localStorage.getItem(STORAGE_LOGGED);          // Já está logado.
        return Session.requestFromSSO || sItem != null || item === "1";
    }

    static get requestFromSSO(): boolean {
        const isSSO = window.document.location.search.toLowerCase().startsWith("?sso=");
        if (isSSO) {
            this.processSSO(window.document.location.search.substr(5));
        }

        return isSSO;
    }

    ///////////////////////////////////////////////////////////

    static set keepConnected(value: boolean) {
        window.localStorage.setItem(STORAGE_KEEP_CONNECTED, value ? "1" : "0");
    }

    static get keepConnected(): boolean {
        return window.localStorage.getItem(STORAGE_KEEP_CONNECTED) === "1";
    }

    static get loginOptions() {
        return {
            authOptions: Session.sessionSettings.authOptions,
            samlLoginUrl: Session.sessionSettings.samlLoginUrl
        };
    }

    static get logoutOptions() {
        return {
            samlLogoutUrl: Session.sessionSettings.samlLogoutUrl
        };
    }

    static get defaultEmailAddress() {
        return Session.sessionSettings.defaultEmailAddress;
    }

    static storeSettings(settings: SessionSettingsModel) {
        Session.appTheme = settings.theme;
        Session.sessionSettings = settings;
    }
    static get tenantId() {
        return Session.sessionSettings.tenantId;
    }

    ///////////////////////////////////////////////////////////

    private static observers: Array<SessionObserverCallback> = [];

    /**
     * Notificação mudanças nos dados do usuário.
     * @param callback
     */
    private static notifyObservers() {
        Session.observers.forEach((v) => v());
    }

    /**
     * Subscreve um callback para notificação quando os dados do usuário forem alterados.
     * @param callback
     */
    static subscribe(callback: SessionObserverCallback) {
        Session.observers.push(callback);
    }

    /**
     * Remove a subscrição para notificação quando os dados do usuário forem alterados.
     * @param callback
     */
    static unsubscribe(callback: SessionObserverCallback) {
        Session.observers = Session.observers.filter(p => p != callback);
    }

    ///////////////////////////////////////////////////////////

    /**
     * Retorna a Versão do Aplicativo
     */
    static get appVersion() {
        return localStorage.getItem(STORAGE_VERSION);
    }

    /**
     * Verifica se há uma nova versão do aplicativo
     */
    static checkNewSystemVersion(releaseNumber: string) {

        const last_version = localStorage.getItem(STORAGE_VERSION);
        if (last_version == null) {
            localStorage.setItem(STORAGE_VERSION, releaseNumber);
            return false;
        }
        if (last_version !== releaseNumber) {
            localStorage.setItem(STORAGE_VERSION, releaseNumber);
            return true;
        }

        return false;
    }
}